"use client";

import { z } from "zod";
import {
  currency,
  Industries,
  industryOptions,
  loanTypes,
  loanUseOptions,
  shortCurrency,
} from "@cxm/lib";
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { DefaultValues } from "../utils/form-helpers";
import { Button } from "../components/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../components/form";
import { Select } from "../components/select";
import { useConfirmDialogContext } from "../components/confirmdialog";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  getApiBorrowersGetGlossaryOptions,
  postApiBorrowersEvaluateFundingOptionsMutation,
  postApiBorrowersOnboardAdditionalBorrowerMutation,
} from "@cxm/client/queries";
import {
  CompanyStage,
  FundingOption,
  FundingTimeline,
  GetApiBorrowersGetGlossaryResponse,
  LandingEvaluationResponse,
  LoanType,
  LoanUse,
} from "@cxm/client";
import { ChevronLeft, ChevronRight, Info, ArrowUp, ArrowDown, ArrowUpDown, CircleHelp } from "lucide-react";
import { Slider } from "../components/slider";
import { Input } from "../components/input";
import { MoneyField, MoneyInput, SelectField } from "./form-inputs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/table";
import { zodResolver } from "@hookform/resolvers/zod";
import Wheel from "./wheel";
import FundabilityBreakdown from "./fundability-breakdown";
import debounce from "debounce";
import { DataRows } from "../utils/table-helpers";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import createRadialScale from "@visx/scale/lib/scales/radial";
const fundingTimelineOptions = [
  {
    label: "As soon as possible",
    value: FundingTimeline.AS_SOON_AS_POSSIBLE,
  },
  { label: "Within 1 month", value: FundingTimeline.WITHIN_ONE_MONTH },
  { label: "Within 3 months", value: FundingTimeline.WITHIN_THREE_MONTHS },
  { label: "Within 6 months", value: FundingTimeline.WITHIN_SIX_MONTHS },
];

const screenOneMetrics = [
  {
    Key: "TTMRevenue",
    Name: "Previous 12 Months Revenue",
    Min: 0,
    Max: 50000000,
    Description: "Total revenue for the past 12 months",
  },
  {
    Key: "TTMEbitda",
    Name: "Previous 12 Months EBITDA",
    Min: -5000000,
    Max: 50000000,
    Description:
      "Earnings before interest, taxes, depreciation, and amortization for the past year",
  },
  {
    Key: "TTMNetIncome",
    Name: "Previous 12 Months Net Income",
    Min: -5000000,
    Max: 50000000,
    Description: "Total profit after all expenses and taxes for the past year",
  },
  {
    Key: "TotalInventory",
    Name: "Inventory Value",
    Min: 0,
    Max: 20000000,
    Description:
      "Estimated value of inventory. Use the second slider to set the value already pledged as collateral.",
    HasSecondKey: true,
    SecondKey: "EncumberedInventory",
    SecondName: "Value of inventory already pledged as collateral",
  },
  {
    Key: "TotalRealEstate",
    Name: "Real Estate Value",
    Min: 0,
    Max: 20000000,
    Description:
      "Estimated value of real estate. Use the second slider to set the value already pledged as collateral.",
    HasSecondKey: true,
    SecondKey: "EncumberedRealEstate",
    SecondName: "Value of real estate already pledged as collateral",
  },
  {
    Key: "TotalEquipment",
    Name: "Equipment Value",
    Min: 0,
    Max: 20000000,
    Description:
      "Estimated value of equipment. Use the second slider to set the value already pledged as collateral.",
    HasSecondKey: true,
    SecondKey: "EncumberedEquipment",
    SecondName: "Value of equipment already pledged as collateral",
  },
  {
    Key: "TotalDebt",
    Name: "Total Debt",
    Min: 0,
    Max: 10000000,
    Description: "Total amount of all business debt",
  },
  {
    Key: "MonthlyDebtService",
    Name: "Monthly Debt Service",
    Min: 0,
    Max: 2000000,
    Description: "Total monthly payments on all debt obligations",
  },
  {
    Key: "MonthlyAr",
    Name: "Average Monthly Accounts Receivable",
    Min: 0,
    Max: 10000000,
    Description:
      "Money owed to your business by customers, averaged over the past 12 months",
  },
  {
    Key: "PO",
    Name: "Average Monthly Purchase Orders",
    Min: 0,
    Max: 10000000,
    Description:
      "Average monthly purchase orders, averaged over the past 12 months",
  },
];

const industryNames = Object.values(Industries).map((
  industry,
) => (industry.displayName));

const getFundabilityScoreStyles = (score: number | undefined) => {
  if (score === undefined) {
    return {
      textColor: "text-gray-400",
      borderColor: "border-gray-200",
      label: "---",
    };
  }

  if (score <= 2) {
    return {
      textColor: "text-red-500",
      borderColor: "border-red-200",
      label: "Poor",
    };
  } else if (score >= 6 && score <= 8) {
    return {
      textColor: "text-green-500",
      borderColor: "border-green-200",
      label: "Good",
    };
  } else if (score >= 3 && score <= 5) {
    return {
      textColor: "text-yellow-500",
      borderColor: "border-yellow-200",
      label: "Fair",
    };
  } else {
    return {
      textColor: "text-blue-500",
      borderColor: "border-blue-200",
      label: "Excellent",
    };
  }
};

const industryEnum = z.enum(industryNames as [string, ...string[]]);

const formOneModel = z.object({
  Industry: industryEnum,
  LoanAmount: z.number(),
  ZipCode: z.string().min(3),
  LoanUse: z.string(),
  FundingTimeline: z.string(),
  CompanyStage: z.string(),
  ...Object.fromEntries(
    screenOneMetrics.flatMap((metric) => [
      [metric.Key, metric.Min == 0 ? z.number().min(0) : z.number()],
      ...(metric.HasSecondKey ? [[metric.SecondKey, z.number().min(0)]] : []),
    ]),
  ),
});

const formTwoModel = z.object({
  LoanUse: z.string(),
  LoanAmount: z.number(),
  FundingTimeline: z.string(),
});

const formTwoDefaults: DefaultValues<FormTwoType> = {
  LoanUse: LoanUse.GROWTH_CAPITAL,
  LoanAmount: 1000000,
  FundingTimeline: FundingTimeline.WITHIN_SIX_MONTHS,
};

type FormTwoType = z.infer<typeof formTwoModel>;
type FormOneType = z.infer<typeof formOneModel>;

const defaults: DefaultValues<FormOneType> = {
  Industry: industryNames[4],
  LoanAmount: 1000000,
  CompanyStage: CompanyStage.GROWTH,
  ...Object.fromEntries(
    screenOneMetrics.flatMap((metric) => [
      [metric.Key, 0],
      ...(metric.HasSecondKey ? [[metric.SecondKey, 0]] : []),
    ]),
  ),
};

type OnboardState = "start" | "calculated" | "done";
type OnboardStep = 1 | 2 | 3 | 4;
type SortField = 'loanType' | 'rate' | 'term' | 'potential' | 'payment' | 'lenders' | 'timeline';
type SortDirection = 'asc' | 'desc' | null;

function OnboardWidget(
  { isLoggedIn, baseUrl, nonWidget, referralTag }: {
    initialGlossary?: GetApiBorrowersGetGlossaryResponse;
    baseUrl?: string;
    isLoggedIn?: boolean;
    nonWidget?: boolean;
    referralTag?: string;
  },
) {
  const [loading, setLoading] = useState<boolean>(false);
  const [hoveredType, setHoveredType] = useState<string>();
  const [onboardState, setOnboardState] = useState<OnboardState>("start");
  const [currentStep, setCurrentStep] = useState<OnboardStep>(1);
  const [lastEvalId, setLastEvalId] = useState<string>();
  const [selectedLoanType, setSelectedLoanType] = useState<LoanType>();
  const [hasSelectedLoanType, setHasSelectedLoanType] = useState(false);
  const [drawBar, setDrawBar] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [resultCalculation, setResultCalculation] = useState<
    LandingEvaluationResponse
  >();
  const [recommendedLoanType, setRecommendedLoanType] = useState<
    LoanType
  >();
  const [topFundingOption, setTopFundingOption] = useState<FundingOption>();
  const [biggestFundingOptions, setBiggestFundingOption] = useState<
    FundingOption
  >();
  const [formOneLastData, setFormOneLastData] = useState<
    Record<string, any>
  >();
  const [isLocating, setIsLocating] = useState(false);
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  const [netIncomeManuallyAdjusted, setNetIncomeManuallyAdjusted] = useState(false);
  const lastSyncedEbitda = useRef<number>(0);

  const form = useForm<FormOneType>({
    resolver: zodResolver(formOneModel),
    defaultValues: defaults,
  });
  const formTwo = useForm<FormTwoType>({
    defaultValues: formTwoDefaults,
  });

  const glossaryQuery = useQuery(
    getApiBorrowersGetGlossaryOptions(),
  );

  const fundingOptionsQuery = useMutation(
    postApiBorrowersEvaluateFundingOptionsMutation(),
  );

  const handleResize = () => {
    setIsMobile(window.innerWidth < 768); // Adjust the breakpoint as needed
  };

  const onboardBorrowerMutation = useMutation(
    postApiBorrowersOnboardAdditionalBorrowerMutation(),
  );

  const confirmDialog = useConfirmDialogContext();

  useEffect(() => {
    if (loading) {
      setTimeout(() => {
        setDrawBar(true);
      }, 10);
    } else {
      setDrawBar(false);
    }
  }, [loading]);

  useEffect(() => {
    handleResize();

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const secondForm = useForm({
    defaultValues: {
      loanType: recommendedLoanType,
      dummyField: "dummy",
    },
  });

  // Debounced calculate score function
  const debouncedCalculateScore = useCallback(
    debounce((values: Record<string, any>, shouldFindBestLoanType = true) => {
      calculateScore(values, shouldFindBestLoanType);
    }, 1000),
    [],
  );

  // Watch form changes and trigger debounced calculation if we've already calculated once
  useEffect(() => {
    if (resultCalculation) {
      const subscription = form.watch((values) => {
        if (values) {
          debouncedCalculateScore(values as Record<string, any>, false);
        }
      });
      return () => subscription.unsubscribe();
    }
  }, [resultCalculation, form, debouncedCalculateScore]);

  // Auto-sync net income with EBITDA if not manually adjusted
  useEffect(() => {
    if (!netIncomeManuallyAdjusted) {
      const ebitda = form.watch("TTMEbitda") ?? 0;
      
      // Only update if EBITDA has actually changed to prevent infinite loops
      if (ebitda !== lastSyncedEbitda.current) {
        form.setValue("TTMNetIncome", ebitda);
        lastSyncedEbitda.current = ebitda;
      }
    }
  }, [form.watch("TTMEbitda"), netIncomeManuallyAdjusted, form]);

  // Reset manual adjustment flag when form is reset
  const resetForm = useCallback(() => {
    form.reset(defaults);
    setNetIncomeManuallyAdjusted(false);
  }, [form]);

  const submitApply = async () => {
    if (!resultCalculation) return;

    if (isLoggedIn) {
      if (
        await confirmDialog.openDialogAsync({
          title: "Create New Company",
          message:
            "Do you want to create a new company profile with this data or update your existing profile?",
          confirmText: "Create New",
          cancelText: "Update Existing",
        })
      ) {
        setLoading(true);
        await onboardBorrowerMutation.mutateAsync({
          query: {
            evalId: lastEvalId,
          },
        });
        window.location.href = `/borrower/profile`;
      } else {
        setLoading(true);

        window.location.href = `/borrower/landing-apply?evalId=${lastEvalId}`;
      }
    } else {
      setLoading(true);

      if (nonWidget) {
        window.location.href = (baseUrl ?? "") +
          `/borrower/landing-apply?evalId=${lastEvalId}`;
      } else {
        window.open(
          (baseUrl ?? "") +
          `/borrower/landing-apply?evalId=${lastEvalId}`,
        );
      }
    }
  };

  const loanCalcs = (loanType: LoanType) => {
    return <>
        {loanType === LoanType.ABL && (
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="font-semibold text-blue-900 mb-2">
              Asset-Based Loan Amount Calculation
            </h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Unencumbered Equipment (90%):</span>
                <span className="font-medium">
                  {currency((form.getValues("TotalEquipment") || 0) - (form.getValues("EncumberedEquipment") || 0) * 0.9)}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Unencumbered Real Estate (75%):</span>
                <span className="font-medium">
                  {currency(((form.getValues("TotalRealEstate") || 0) - (form.getValues("EncumberedRealEstate") || 0)) * 0.75)}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Unencumbered Inventory (80%):</span>
                <span className="font-medium">
                  {currency(((form.getValues("TotalInventory") || 0) - (form.getValues("EncumberedInventory") || 0)) * 0.8)}
                </span>
              </div>
              <hr className="border-blue-300" />
              <div className="flex justify-between font-semibold text-blue-900">
                <span>Estimated Maximum Loan Amount:</span>
                <span>
                  {currency(
                    ((form.getValues("TotalEquipment") || 0) - (form.getValues("EncumberedEquipment") || 0)) * 1.00 +
                    ((form.getValues("TotalRealEstate") || 0) - (form.getValues("EncumberedRealEstate") || 0)) * 0.75 +
                    ((form.getValues("TotalInventory") || 0) - (form.getValues("EncumberedInventory") || 0)) * 0.9
                  )}
                </span>
              </div>
            </div>
          </div>
        )}
        {loanType === LoanType.AR_FACTORING && (
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="font-semibold text-blue-900 mb-2">
              Accounts Receivable Factoring
            </h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Average Monthly Accounts Receivable:</span>
                <span className="font-medium">
                  {currency((form.getValues("MonthlyAr") || 0))}
                </span>
              </div>
              <div className="flex justify-end">
                <span>x3 (90 Days)</span>
              </div>
              <div className="flex justify-between">
                <span>Estimated Total Accounts Receivable:</span>
                <span className="font-medium">
                  {currency((form.getValues("MonthlyAr") || 0) * 3)}
                </span>
              </div>
              <hr className="border-blue-300" />
              <div className="flex justify-between font-semibold text-blue-900">
                <span>Estimated Potential Advance (85%):</span>
                <span>{currency(form.getValues("MonthlyAr") * 3 * 0.85)}</span>
              </div>
            </div>
          </div>
        )}
        {loanType === LoanType.PURCHASE_ORDER && (
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="font-semibold text-blue-900 mb-2">
              Purchase Order Financing
            </h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Average Monthly Purchase Orders:</span>
                <span className="font-medium">
                  {currency((form.getValues("PO") || 0))}
                </span>
              </div>
              <div className="flex justify-end">
                <span>x3 (90 Days)</span>
              </div>
              <div className="flex justify-between">
                <span>Estimated Total Purchase Orders:</span>
                <span className="font-medium">
                  {currency((form.getValues("PO") || 0) * 3)}
                </span>
              </div>
              <hr className="border-blue-300" />
              <div className="flex justify-between font-semibold text-blue-900">
                <span>Estimated Potential Advance (85%):</span>
                <span>{currency(form.getValues("PO") * 3 * 0.85)}</span>
              </div>
            </div>
          </div>
        )}
    </>
  }

  const loanTypeDescriptions = 
    loanTypes.reduce((acc, loanType) => {
      acc[loanType.key] = <div>
        <p className="text-gray-600 whitespace-pre-line">{loanType.longDescription}</p>
        {loanCalcs(loanType.key)}
      </div>;
      return acc;
    }, {} as { [key: string]: React.ReactNode });

  const renderStepContent = (step: OnboardStep) => {
    switch (step) {
      case 1:
        return (
          <div className="bg-white rounded-lg shadow-md p-6 pt-4">
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(async (values: FormOneType) => {
                  (values as Record<string, any>)["LoanType"] =
                    selectedLoanType;
                  // Only use debounce if we've already calculated once
                  if (resultCalculation) {
                    debouncedCalculateScore(values, false);
                  } else {
                    calculateScore(values);
                  }
                })}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="ZipCode"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-gray-700 font-medium">
                          Zip Code
                        </FormLabel>
                        <div className="flex gap-2">
                          <FormControl>
                            <Input {...field} className="w-full" />
                          </FormControl>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                              setIsLocating(true);
                              navigator.geolocation.getCurrentPosition(
                                async (position) => {
                                  try {
                                    const response = await fetch(
                                      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${position.coords.latitude}&lon=${position.coords.longitude}`,
                                    );
                                    const data = await response.json();
                                    if (data.address && data.address.postcode) {
                                      form.setValue(
                                        "ZipCode",
                                        data.address.postcode,
                                      );
                                    }
                                  } catch (error) {
                                    console.error(
                                      "Error getting location:",
                                      error,
                                    );
                                  } finally {
                                    setIsLocating(false);
                                  }
                                },
                                (error) => {
                                  console.error(
                                    "Error getting location:",
                                    error,
                                  );
                                  setIsLocating(false);
                                },
                                {
                                  enableHighAccuracy: true,
                                  timeout: 5000,
                                  maximumAge: 0,
                                },
                              );
                            }}
                            disabled={isLocating}
                            className="whitespace-nowrap"
                          >
                            {isLocating
                              ? (
                                <div className="flex items-center gap-2">
                                  <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                                  <span>Locating...</span>
                                </div>
                              )
                              : (
                                <div className="flex items-center gap-2">
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  >
                                    <circle cx="12" cy="12" r="10" />
                                    <circle cx="12" cy="12" r="6" />
                                    <circle cx="12" cy="12" r="2" />
                                  </svg>
                                  <span>Use My Location</span>
                                </div>
                              )}
                          </Button>
                        </div>
                        <FormMessage className="text-sm" />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="CompanyStage"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-gray-700 font-medium">
                          Company Stage
                        </FormLabel>
                        <Select
                          value={field.value
                            ? { label: field.value, value: field.value }
                            : null}
                          onChange={(val: any) => field.onChange(val?.value)}
                          options={[
                            {
                              label: "Early (1-2 years)",
                              value: CompanyStage.EARLY,
                            },
                            {
                              label: "Growth (3-10 years)",
                              value: CompanyStage.GROWTH,
                            },
                            {
                              label: "Mature (10+ years)",
                              value: CompanyStage.MATURE,
                            },
                          ]}
                          className="w-full"
                        />
                        <FormMessage className="text-sm" />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="Industry"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-gray-700 font-medium">
                        Industry
                      </FormLabel>
                      <Select
                        value={field.value
                          ? { label: field.value, value: field.value }
                          : null}
                        onChange={(val: any) => field.onChange(val?.value)}
                        options={industryOptions}
                        className="w-full"
                      />
                      <FormMessage className="text-sm" />
                    </FormItem>
                  )}
                />

                <h2 className="text-lg font-semibold mt-2">
                  Funds Needed
                </h2>
                <div className="space-y-3">
                  <MoneyField label="Loan Amount" name="LoanAmount" />
                  <div className="grid grid-cols-2 gap-4">
                    <SelectField
                      label="Loan Use"
                      name="LoanUse"
                      options={loanUseOptions}
                    />
                    <SelectField
                      label="Funding Timeline"
                      name="FundingTimeline"
                      options={fundingTimelineOptions}
                    />
                  </div>
                </div>
                <h2 className="text-lg font-semibold mt-2">
                  Company Metrics
                </h2>
                <div className="space-y-2">
                  <div className="grid grid-cols-1">
                    {screenOneMetrics.map(renderMetricField)}
                  </div>
                </div>

                <Button
                  disabled={fundingOptionsQuery.isPending}
                  type="submit"
                  className="w-full text-lg font-medium p-6 bg-green-500 hover:bg-green-600 mt-4"
                >
                  {fundingOptionsQuery.isPending
                    ? "Calculating..."
                    : (resultCalculation
                      ? "Recalculate"
                      : "See Funding Options")}
                </Button>
              </form>
            </Form>
          </div>
        );
      case 3:
        return (
          <div className="bg-white rounded-lg shadow-md p-6">
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(async (values: FormOneType) => {
                  (values as Record<string, any>)["LoanType"] =
                    selectedLoanType;
                  calculateScore(values);
                })}
              >
                <div className="space-y-2">
                  <h2 className="text-xl font-semibold mb-4">
                    Tell us about your finances
                  </h2>
                  <div className="grid grid-cols-1">
                    {screenOneMetrics.map(renderMetricField)}
                  </div>
                </div>

                {currentStep == 1
                  ? (
                    <Button
                      disabled={fundingOptionsQuery.isPending}
                      type="submit"
                      className="w-full text-lg font-medium p-6 bg-green-500 hover:bg-green-600 mt-4"
                    >
                      {fundingOptionsQuery.isPending
                        ? "Calculating..."
                        : "Get Started"}
                    </Button>
                  )
                  : (
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full text-lg font-medium p-6 mt-4"
                      onClick={(e) => {
                        e.preventDefault();
                        setCurrentStep(1);
                      }}
                    >
                      Back
                    </Button>
                  )}
              </form>
            </Form>
          </div>
        );
      case 2:
        return (
          <Form {...formTwo}>
            <form>
              <div className="">
                <Wheel
                  size={400}
                  selectedType={selectedLoanType}
                  onSelectType={(t) => {
                    // Only allow selection if the loan type is in the funding options
                    const loanTypeId = loanTypes.find((x) => x.name == t)?.key;
                    if (
                      !resultCalculation?.fundingOptions?.some((option) =>
                        option.loanType === loanTypeId
                      )
                    ) {
                      return;
                    }

                    setHasSelectedLoanType(true);
                    secondForm.setValue("loanType", loanTypeId ?? "Abl");
                    const data = formOneLastData || {};
                    data["LoanType"] = loanTypeId;
                    setSelectedLoanType(loanTypeId);

                    // Find the funding option for the selected loan type
                    if (resultCalculation?.fundingOptions) {
                      const selectedOption = resultCalculation.fundingOptions
                        .find(
                          (option) => option.loanType === loanTypeId,
                        );
                      if (selectedOption) {
                        setTopFundingOption(selectedOption);
                      }
                    }
                  }}
                  slices={loanTypes.map((loanType) => ({
                    ...loanType,
                    disabled: resultCalculation?.fundingOptions
                      ? !resultCalculation.fundingOptions.some((option) =>
                        option.loanType === loanType.key
                      )
                      : false,
                  }))}
                  loanTypeDescriptions={loanTypeDescriptions}
                  onHoverType={(t) => setHoveredType(t)}
                />
                <div
                  className={`bg-white my-2 rounded-lg shadow-md border-2 ${getFundabilityScoreStyles(
                    resultCalculation?.fundabilityScore,
                  ).borderColor
                    }`}
                >
                  <div className="flex flex-col items-center relative p-4 py-8 h-[240px] gap-8 justify-center">
                    <h1 className="text-xl font-bold text-center mb-1">
                      Company Fundability Score and Funding Choices
                    </h1>
                    {resultCalculation?.fundabilityScore !== undefined
                      ? (
                        <div className="flex flex-col items-center w-full ">
                          <div className="relative w-full max-w-md flex flex-col items-center space-y-4">
                            {/* Score text */}
                            <div>
                              <span
                                className={`text-4xl font-bold text-centerfont-medium ${getFundabilityScoreStyles(
                                  resultCalculation.fundabilityScore,
                                ).textColor
                                  }`}
                              >
                                {getFundabilityScoreStyles(
                                  resultCalculation.fundabilityScore,
                                ).label}
                              </span>
                              <span
                                className={`text-2xl font-bold text-centerfont-medium ${getFundabilityScoreStyles(
                                  resultCalculation.fundabilityScore,
                                ).textColor
                                  }`}
                              >
                                {" "}({resultCalculation.fundabilityScore}
                              </span>
                              <span
                                className={` text-lg font-bold text-centerfont-medium ${getFundabilityScoreStyles(
                                  resultCalculation.fundabilityScore,
                                ).textColor
                                  }`}
                              >
                                /10
                              </span>
                              <span
                                className={`text-2xl font-bold text-centerfont-medium ${getFundabilityScoreStyles(
                                  resultCalculation.fundabilityScore,
                                ).textColor
                                  }`}
                              >
                                )
                              </span>
                            </div>
                            {/* Score bar */}
                            <div className="w-full h-4 bg-gray-200 rounded-full overflow-hidden mb-2">
                              <div
                                className="h-full transition-all duration-1000 ease-out rounded-full"
                                style={{
                                  width: `${(resultCalculation.fundabilityScore / 10) *
                                    100
                                    }%`,
                                  background:
                                    "linear-gradient(to right, #ef4444, #eab308, #22c55e)",
                                }}
                              />
                            </div>
                          </div>
                          <p className="text-sm text-gray-600 text-center max-w-md w-full">
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button
                                  variant="outline"
                                  className="h-[30px] inline-flex items-center ml-1 absolute top-1 right-1"
                                >
                                  <Info className="h-4 w-4" />
                                  <span className="ml-1">
                                    Understand Your Score
                                  </span>
                                </Button>
                              </DialogTrigger>
                              <DialogContent className="max-w-2xl">
                                <DialogHeader>
                                  <DialogTitle>Key Insights</DialogTitle>
                                </DialogHeader>
                                <div className="mt-4">
                                  <FundabilityBreakdown
                                    narrative={resultCalculation.narrative ??
                                      []}
                                  />
                                </div>
                              </DialogContent>
                            </Dialog>
                          </p>
                        </div>
                      )
                      : (
                        <div className="flex flex-col justify-center items-center pt-4 pb-1">
                          <span className="text-4xl font-bold ">
                            ---
                          </span>
                          <span className="text-sm font-bold ">
                            Your fundability score will be based on the metrics entered.
                          </span>
                        </div>
                      )}
                  </div>
                </div>
                {resultCalculation && (
                  <div className="bg-primary text-white p-2 rounded-md mb-2 text-center">
                    <p>
                      Select slices on the loan wheel or rows in the table to explore funding options.
                    </p>
                  </div>
                )}
                {!resultCalculation && (
                  <div className="bg-primary text-white rounded-lg shadow-md p-4 border border-gray-300">
                    <h2 className="text-xl font-semibold mb-2">
                      Indicative Loan Options and Terms
                    </h2>
                    <DataRows
                      className="mt-4 text-xl"
                      data={resultCalculation || {
                        fundingOptions: "---",
                        loanType: "---",
                        interestRate: "---",
                        monthlyPayment: "---",
                        maturityInMonths: "---",
                        potentialFundsAvailable: "---",
                        matchedLenders: "---",
                        daysToFund: "---",
                      }}
                      fields={{
                        maturityInMonths: {
                          label: "Estimated Term",
                        },
                        interestRate: {
                          label: "Estimated Rate",
                        },
                        monthlyPayment: {
                          label: "Estimated Monthly Payment",
                        },
                      }}
                    />
                  </div>
                )}
                {!resultCalculation && (
                  <div className="bg-white rounded-lg shadow-md p-4 hidden">
                    <h2 className="text-lg font-semibold mb-2">
                      Enter your metrics to see funding options
                    </h2>
                    <div className="space-y-2">
                      {/* Ghost funding option 1 */}
                      <div className="p-4 rounded-lg bg-gray-50 animate-pulse">
                        <div className="flex items-center justify-between mb-2">
                          <div className="h-5 rounded w-1/3">
                            Funding Option 1
                          </div>
                          <div className="h-3 bg-gray-200 rounded w-1/4"></div>
                        </div>
                        <div className="h-3 bg-gray-200 rounded w-2/3 mb-2">
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="grid grid-cols-2 gap-2 flex-1">
                            <div>
                              <div className="h-3 rounded w-1/2 mb-1 text-xs whitespace-nowrap">
                                Estimated Interest Rate
                              </div>
                              <div className="h-4 bg-gray-200 rounded w-1/3">
                              </div>
                            </div>
                            <div>
                              <div className="h-3 rounded w-1/2 mb-1 text-xs whitespace-nowrap">
                                Term
                              </div>
                              <div className="h-4 bg-gray-200 rounded w-1/3">
                              </div>
                            </div>
                            <div>
                              <div className="h-3 rounded w-1/2 mb-1 text-xs whitespace-nowrap">
                                Potential Available
                              </div>
                              <div className="h-4 bg-gray-200 rounded w-1/3">
                              </div>
                            </div>
                            <div>
                              <div className="h-3 rounded w-1/2 mb-1 text-xs whitespace-nowrap">
                                Estimated Payment
                              </div>
                              <div className="h-4 bg-gray-200 rounded w-1/3">
                              </div>
                            </div>
                          </div>
                          <div className="h-8 bg-gray-200 rounded w-24"></div>
                        </div>
                      </div>
                      {/* Ghost funding option 2 */}
                      <div className="p-4 rounded-lg bg-gray-50 animate-pulse">
                        <div className="flex items-center justify-between mb-2">
                          <div className="h-4 rounded w-1/3">
                            Funding Option 2
                          </div>
                          <div className="h-3 bg-gray-200 rounded w-1/4"></div>
                        </div>
                        <div className="h-3 bg-gray-200 rounded w-2/3 mb-2">
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="grid grid-cols-2 gap-2 flex-1">
                            <div>
                              <div className="h-3 rounded w-1/2 mb-1 text-xs whitespace-nowrap">
                                Estimated Interest Rate
                              </div>
                              <div className="h-4 bg-gray-200 rounded w-1/3">
                              </div>
                            </div>
                            <div>
                              <div className="h-3 rounded w-1/2 mb-1 text-xs whitespace-nowrap">
                                Term
                              </div>
                              <div className="h-4 bg-gray-200 rounded w-1/3">
                              </div>
                            </div>
                            <div>
                              <div className="h-3 rounded w-1/2 mb-1 text-xs whitespace-nowrap">
                                Potential Available
                              </div>
                              <div className="h-4 bg-gray-200 rounded w-1/3">
                              </div>
                            </div>
                            <div>
                              <div className="h-3 rounded w-1/2 mb-1 text-xs whitespace-nowrap">
                                Estimated Payment
                              </div>
                              <div className="h-4 bg-gray-200 rounded w-1/3">
                              </div>
                            </div>
                          </div>
                          <div className="h-8 bg-gray-200 rounded w-24"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {/* Add Loan Details Section */}
                {resultCalculation?.fundingOptions &&
                  resultCalculation.fundingOptions.length > 0 && (
                    <div className="bg-white rounded-lg shadow-md p-4">
                      <h2 className="text-lg font-semibold mb-2">
                        {resultCalculation.fundingOptions.length} Funding Options
                      </h2>
                      <h2 className="text-black text-sm my-1">Click each option to learn more.</h2>
                      {!biggestFundingOptions?.highAmount ||
                        biggestFundingOptions.highAmount <
                        form.getValues("LoanAmount")
                        ? (
                          <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-md">
                            <p className="text-sm text-orange-700">
                              No single funding option is likely to get you{" "}
                              {currency(form.getValues("LoanAmount"))}. A
                              combination of funding options may be required.
                            </p>
                          </div>
                        )
                        : null}
                      <div className="max-h-[455px] overflow-y-auto">
                        <Table>
                          <TableHeader>
                            <TableRow className="text-black">
                              <TableHead className="w-[200px] px-2">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  className="px-0 h-auto font-medium hover:bg-transparent text-black"
                                  onClick={() => handleSort('loanType')}
                                >
                                  Loan Type
                                  {getSortIcon('loanType')}
                                </Button>
                              </TableHead>
                              <TableHead className="px-2">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  className="px-0 h-auto font-medium hover:bg-transparent text-black"
                                  onClick={() => handleSort('potential')}
                                >
                                  Max Potential
                                  {getSortIcon('potential')}
                                </Button>
                              </TableHead>
                              <TableHead className="px-2">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  className="px-0 h-auto font-medium hover:bg-transparent text-black"
                                  onClick={() => handleSort('payment')}
                                >
                                  Monthly Payment
                                  {getSortIcon('payment')}
                                </Button>
                              </TableHead>
                              <TableHead className="px-2">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  className="px-0 h-auto font-medium hover:bg-transparent text-black"
                                  onClick={() => handleSort('rate')}
                                >
                                  Rate
                                  {getSortIcon('rate')}
                                </Button>
                              </TableHead>
                              <TableHead className="px-2">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  className="px-0 h-auto font-medium hover:bg-transparent text-black"
                                  onClick={() => handleSort('term')}
                                >
                                  Term
                                  {getSortIcon('term')}
                                </Button>
                              </TableHead>
                              <TableHead className="px-2">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  className="px-0 h-auto font-medium hover:bg-transparent text-black"
                                  onClick={() => handleSort('lenders')}
                                >
                                  Matched Lenders
                                  {getSortIcon('lenders')}
                                </Button>
                              </TableHead>
                              <TableHead className="px-2">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  className="px-0 h-auto font-medium hover:bg-transparent text-black"
                                  onClick={() => handleSort('timeline')}
                                >
                                  Timeline
                                  {getSortIcon('timeline')}
                                </Button>
                              </TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {sortFundingOptions(resultCalculation.fundingOptions).map((option, index) => {
                              const loanType = loanTypes.find((x) =>
                                x.key === option.loanType
                              );
                              const matchedLenderCount =
                                resultCalculation?.matchedLenders
                                ?.[option.loanType ?? LoanType.ABL] || 0;
                              const isTopOption = option === topFundingOption;
                              return (
                                <Dialog key={index}>
                                  <DialogTrigger asChild>
                                    <TableRow
                                      className={`cursor-pointer h-[48px] ${isTopOption
                                        ? "bg-green-50 border-green-200 hover:bg-green-100"
                                        : "hover:bg-gray-50"
                                        }`}
                                    >
                                      <TableCell className="font-medium text-center px-2">
                                        <div>
                                          <div className="font-semibold text-gray-900">
                                            {loanType?.name || option.loanType}
                                          </div>
                                        </div>
                                      </TableCell>
                                      <TableCell className="text-center px-2">
                                        {(option.lowAmount && option.highAmount) ? (
                                          <div>
                                            <div className="font-semibold text-gray-900">
                                              {shortCurrency(option.highAmount)}
                                            </div>
                                          </div>
                                        ) : (
                                          <span className="text-gray-400">—</span>
                                        )}
                                      </TableCell>
                                      <TableCell className="text-center px-2">
                                        {option.lowPayment ? (
                                          <div>
                                            <div className="font-semibold text-gray-900">
                                              {currency(option.lowPayment)}
                                            </div>
                                          </div>
                                        ) : (
                                          <span className="text-gray-400">—</span>
                                        )}
                                      </TableCell>
                                      <TableCell className="text-center px-2">
                                        <div className="font-semibold text-gray-900">
                                          {(option.loanType == LoanType.AR_FACTORING || option.loanType == LoanType.PURCHASE_ORDER) ?
                                            "--" :
                                            `${Math.round((option.estimatedRate ?? 0) * 100)}%`}
                                        </div>
                                        <div className="text-xs text-gray-500">
                                          {(option.loanType == LoanType.AR_FACTORING || option.loanType == LoanType.PURCHASE_ORDER)
                                            ? "Fee-Based"
                                            : "Interest"
                                          }
                                        </div>
                                      </TableCell>
                                      <TableCell className="text-center px-2">
                                        <div className="font-semibold text-gray-900">
                                          {option.highMaturity}
                                        </div>
                                        <div className="text-xs text-gray-500">Months</div>
                                      </TableCell>
                                      <TableCell className="text-center px-2">
                                        {matchedLenderCount > 0 ? (
                                          <div>
                                            <div className="font-semibold text-green-600">
                                              {matchedLenderCount}
                                            </div>
                                          </div>
                                        ) : (
                                          <div>
                                            <div className="font-semibold text-gray-400">0</div>
                                          </div>
                                        )}
                                      </TableCell>
                                      <TableCell className="text-center px-2">
                                        <div className="font-semibold text-gray-900">
                                          {option.lowTime} - {option.highTime}
                                        </div>
                                        <div className="text-xs text-gray-500">Days</div>
                                      </TableCell>
                                    </TableRow>
                                  </DialogTrigger>
                                  <DialogContent className="max-w-2xl">
                                    <DialogHeader>
                                      <DialogTitle>
                                        {loanType?.fullName}
                                      </DialogTitle>
                                    </DialogHeader>
                                    <div className="mt-4">
                                      {loanTypeDescriptions[loanType?.id ?? LoanType.ABL]}
                                    </div>
                                  </DialogContent>
                                </Dialog>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  )}
                {currentStep == 2
                  ? (
                    <Button
                      onClick={form.handleSubmit(
                        async (values: FormOneType) => {
                          submitApply();
                        },
                      )}
                      className="w-full text-lg font-medium p-6 bg-green-500 hover:bg-green-600 mt-2"
                    >
                      Apply Now
                      <ChevronRight width={18} height={18} />
                    </Button>
                  )
                  : <div></div>}
                {(isMobile && currentStep == 2)
                  ? (
                    <Button
                      className="w-full text-lg font-medium p-6"
                      variant={"outline"}
                      onClick={() => setCurrentStep(1)}
                    >
                      <ChevronLeft width={18} height={18} />
                      Back
                    </Button>
                  )
                  : <div></div>}
              </div>
            </form>
          </Form>
        );
      case 4:
        return (
          <div className="space-y-2">
            <Button
              onClick={submitApply}
              className="w-full text-lg font-medium p-6 bg-green-500 hover:bg-green-600"
            >
              Get Started <ChevronRight width={18} height={18} />
            </Button>
          </div>
        );
    }
  };

  const calculateScore = async (
    values: Record<string, any>,
    shouldFindBestLoanType = true,
  ) => {
    setLoading(true);
    setFormOneLastData(values);

    values["ReferralTag"] = referralTag;
    values["UnencumberedEquipment"] = values["TotalEquipment"] -
      values["EncumberedEquipment"];
    values["UnencumberedInventory"] = values["TotalInventory"] -
      values["EncumberedInventory"];
    values["UnencumberedRealEstate"] = values["TotalRealEstate"] -
      values["EncumberedEquipment"];

    setLoading(false);

    setCurrentStep(2);

    values["LoanUse"] = [values["LoanUse"]];

    const fundingOptions = await fundingOptionsQuery
      .mutateAsync({
        body: values,
      });
    setResultCalculation(fundingOptions);

    // Find the top funding option based on criteria
    const loanAmount = values.LoanAmount;
    const matchedOptions =
      fundingOptions.fundingOptions?.filter((option) =>
        (fundingOptions.matchedLenders?.[option.loanType ?? LoanType.ABL] ??
          0) > 0
      ) || [];

    // Split options into PO/AR and non-PO/AR
    const poArOptions = matchedOptions.filter(option => 
      option.loanType === LoanType.PURCHASE_ORDER || 
      option.loanType === LoanType.AR_FACTORING
    );
    const otherOptions = matchedOptions.filter(option => 
      option.loanType !== LoanType.PURCHASE_ORDER && 
      option.loanType !== LoanType.AR_FACTORING
    );

    // Sort both groups by payment amount
    const sortedPoArOptions = poArOptions.sort((a, b) =>
      (a.lowPayment ?? 1000000) - (b.lowPayment ?? 1000000)
    );
    const sortedOtherOptions = otherOptions.sort((a, b) =>
      (a.lowPayment ?? 1000000) - (b.lowPayment ?? 1000000)
    );

    // First try to find a non-PO/AR option that covers the full amount
    const fullCoverageOtherOption = sortedOtherOptions?.find((option) =>
      (option.highAmount ?? 0) >= loanAmount
    );

    // If no full coverage in other options, try PO/AR options
    const fullCoveragePoArOption = sortedPoArOptions?.find((option) =>
      (option.highAmount ?? 0) >= loanAmount
    );

    // If still no full coverage, use the quickest option from other options first
    const quickestOtherOption = sortedOtherOptions?.[0];
    const quickestPoArOption = sortedPoArOptions?.[0];

    // Select the best option in order of preference:
    // 1. Full coverage non-PO/AR option
    // 2. Full coverage PO/AR option
    // 3. Quickest non-PO/AR option
    // 4. Quickest PO/AR option
    const bestOption = fullCoverageOtherOption || 
                      fullCoveragePoArOption || 
                      quickestOtherOption || 
                      quickestPoArOption;

    setTopFundingOption(bestOption);
    setBiggestFundingOption(
      matchedOptions.sort((a, b) =>
        (b.highAmount ?? 0) - (a.highAmount ?? 0)
      )[0]
    );
    setSelectedLoanType(
      bestOption?.loanType ??
        matchedOptions.sort((a, b) =>
          (b.highAmount ?? 0) - (a.highAmount ?? 0)
        )[0]?.loanType ??
        LoanType.ABL
    );
    setLastEvalId(fundingOptions.evaluationId ?? "");

    setOnboardState("calculated");
  };

  const renderMetricField = (
    metric: typeof screenOneMetrics[0],
    index: number,
  ) => {
    const isYearsInBusiness = metric.Name === "Years in Business";
    const isCurrencyField = !isYearsInBusiness;
    const isMonthlyDebtService = metric.Key === "MonthlyDebtService";

    return (
      <FormField
        key={metric.Key}
        control={form.control}
        name={metric.Key as any}
        render={({ field }) => (
          <FormItem className="">
            <div className="flex justify-between items-start">
              <div className="">
                <FormLabel className="text-base">
                  {index + 1}. {metric.Name}
                </FormLabel>
                <p className="text-xs">
                  {metric.Description}
                </p>
                {/* These were asked to be hidden */}
                {metric.Key === "TTMNetIncome" && !netIncomeManuallyAdjusted && (
                  <p className="text-xs text-blue-600 font-medium flex items-center gap-1 hidden">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Synced with EBITDA
                  </p>
                )}
                {metric.Key === "TTMNetIncome" && netIncomeManuallyAdjusted && (
                  <button
                    type="button"
                    onClick={() => {
                      setNetIncomeManuallyAdjusted(false);
                      const ebitda = form.watch("TTMEbitda") ?? 0;
                      form.setValue("TTMNetIncome", ebitda);
                      lastSyncedEbitda.current = ebitda;
                    }}
                    className="text-xs text-blue-600 hover:text-blue-800 underline hidden"
                  >
                    Reset to sync with EBITDA
                  </button>
                )}
              </div>
            </div>
            {metric.HasSecondKey
              ? (
                <div className="space-y-1">
                  <div className="flex items-center space-x-2 mt-2">
                    <Slider
                      value={[field.value]}
                      onValueChange={(value) => {
                        field.onChange(value[0]);
                        // If total value is reduced below unencumbered value, adjust unencumbered value
                        const currentUnencumbered =
                          form.watch(metric.SecondKey as any) ?? 0;
                        if (currentUnencumbered > (value[0] ?? 0)) {
                          form.setValue(metric.SecondKey as any, value[0]);
                        }
                      }}
                      min={metric.Min}
                      max={metric.Max}
                      step={100000}
                      className="flex-1"
                    />
                    <div className="w-32">
                      <MoneyInput
                        className="h-[30px]"
                        value={field.value}
                        onChange={(e) => {
                          field.onChange(e);
                          // If total value is reduced below unencumbered value, adjust unencumbered value
                          const currentUnencumbered =
                            form.watch(metric.SecondKey as any) ?? 0;
                          if (currentUnencumbered > e) {
                            form.setValue(metric.SecondKey as any, e);
                          }
                        }}
                      />
                    </div>
                  </div>
                  {field.value > 0 && (
                    <>
                      <p className="text-xs">
                        <b>{metric.SecondName}</b>
                      </p>
                      <div className="flex items-center space-x-2">
                        <Slider
                          value={[form.watch(metric.SecondKey as any) ?? 0]}
                          onValueChange={(value) => {
                            const totalValue = field.value ?? 0;
                            const unencumberedValue = Math.min(
                              value[0] ?? 0,
                              totalValue,
                            );
                            form.setValue(
                              metric.SecondKey as any,
                              unencumberedValue,
                            );
                          }}
                          min={metric.Min}
                          max={field.value ?? 0}
                          step={100000}
                          className="flex-1"
                        />
                        <div className="w-32">
                          <MoneyInput
                            className="h-[30px]"
                            value={form.watch(metric.SecondKey as any) ?? 0}
                            onChange={(e) => {
                              const totalValue = field.value ?? 0;
                              const unencumberedValue = Math.min(e, totalValue);
                              form.setValue(
                                metric.SecondKey as any,
                                unencumberedValue,
                              );
                            }}
                          />
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )
              : (
                <div className="flex items-center space-x-2">
                  <Slider
                    value={[field.value]}
                    onValueChange={(value) => {
                      // For EBITDA, ensure it's not higher than revenue
                      if (metric.Key === "TTMEbitda") {
                        const revenue = form.watch("TTMRevenue") ?? 0;
                        const ebitda = Math.min(value[0] ?? 0, revenue);
                        field.onChange(ebitda);
                      } else if (metric.Key === "TTMNetIncome") {
                        // Mark net income as manually adjusted when user interacts with slider
                        setNetIncomeManuallyAdjusted(true);
                        field.onChange(value[0]);
                      } else {
                        field.onChange(value[0]);
                      }
                    }}
                    min={metric.Min}
                    max={metric.Key === "TTMEbitda"
                      ? form.watch("TTMRevenue") ?? metric.Max
                      : metric.Max}
                    step={isYearsInBusiness
                      ? 1
                      : isMonthlyDebtService
                        ? 10000
                        : 100000}
                    className="flex-1"
                  />
                  <div className="w-32">
                    {isCurrencyField
                      ? (
                        <MoneyInput
                          className="h-[30px]"
                          value={field.value}
                          onChange={(e) => {
                            // For EBITDA, ensure it's not higher than revenue
                            if (metric.Key === "TTMEbitda") {
                              const revenue = form.watch("TTMRevenue") ?? 0;
                              const ebitda = Math.min(e, revenue);
                              field.onChange(ebitda);
                            } else if (metric.Key === "TTMNetIncome") {
                              // Mark net income as manually adjusted when user interacts with input
                              setNetIncomeManuallyAdjusted(true);
                              field.onChange(e);
                            } else {
                              field.onChange(e);
                            }
                          }}
                        />
                      )
                      : (
                        <Input
                          type="number"
                          min={metric.Min}
                          max={metric.Max}
                          step={isYearsInBusiness
                            ? 1
                            : isMonthlyDebtService
                              ? 10000
                              : 250000}
                          value={field.value}
                          onChange={(e) =>
                            field.onChange(Number(e.target.value))}
                          placeholder={isCurrencyField ? "$0" : "0"}
                        />
                      )}
                  </div>
                </div>
              )}
            <FormMessage className="text-xs" />
          </FormItem>
        )}
      />
    );
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Cycle through: asc -> desc -> null
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortField(null);
        setSortDirection(null);
      } else {
        setSortDirection('asc');
      }
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="ml-1 h-4 w-4 text-gray-400" />;
    }
    if (sortDirection === 'asc') {
      return <ArrowUp className="ml-1 h-4 w-4" />;
    }
    if (sortDirection === 'desc') {
      return <ArrowDown className="ml-1 h-4 w-4" />;
    }
    return <ArrowUpDown className="ml-1 h-4 w-4 text-gray-400" />;
  };

  const sortFundingOptions = (options: FundingOption[]) => {
    if (!sortField || !sortDirection) {
      // Default sorting logic when no manual sort is applied
      return options.sort((a, b) => {
        // Otherwise sort by matched lenders and funding time
        let aVal = (resultCalculation?.matchedLenders
          ?.[a.loanType ?? LoanType.ABL] ?? 0) > 0
          ? (a.lowPayment ?? 1000000)
          : 900000 + (a.lowPayment ?? 1000000);

        // push po/ar to the bottom
        if (a.loanType == LoanType.AR_FACTORING || a.loanType == LoanType.PURCHASE_ORDER) {
          aVal += 80000000;
        }

        let bVal = (resultCalculation?.matchedLenders
          ?.[b.loanType ?? LoanType.ABL] ?? 0) > 0
          ? (b.lowPayment ?? 1000000)
          : 900000 + (b.lowPayment ?? 1000000);

        // push po/ar to the bottom
        if (b.loanType == LoanType.AR_FACTORING || b.loanType == LoanType.PURCHASE_ORDER) {
          bVal += 80000000;
        }

        return aVal - bVal;
      });
    }

    const sortedOptions = [...options].sort((a, b) => {
      let aVal: any, bVal: any;

      switch (sortField) {
        case 'loanType':
          const aLoanType = loanTypes.find(x => x.key === a.loanType);
          const bLoanType = loanTypes.find(x => x.key === b.loanType);
          aVal = aLoanType?.name || a.loanType || '';
          bVal = bLoanType?.name || b.loanType || '';
          break;
        case 'rate':
          aVal = a.estimatedRate ?? 0;
          bVal = b.estimatedRate ?? 0;
          break;
        case 'term':
          aVal = a.highMaturity ?? 0;
          bVal = b.highMaturity ?? 0;
          break;
        case 'potential':
          aVal = a.highAmount ?? 0;
          bVal = b.highAmount ?? 0;
          break;
        case 'payment':
          aVal = a.lowPayment ?? 100000;
          bVal = b.lowPayment ?? 100000;
          break;
        case 'lenders':
          aVal = resultCalculation?.matchedLenders?.[a.loanType ?? LoanType.ABL] ?? 0;
          bVal = resultCalculation?.matchedLenders?.[b.loanType ?? LoanType.ABL] ?? 0;
          break;
        case 'timeline':
          aVal = a.lowTime ?? 0;
          bVal = b.lowTime ?? 0;
          break;
        default:
          return 0;
      }

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortDirection === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });

    return sortedOptions;
  };

  if (onboardState === "done") {
    return <div>Done!</div>;
  }

  return (
    <div className="flex flex-col space-y-2 relative">
      <div className="w-full bg-primary text-white text-center text-4xl py-4 hidden">
        <b>Jim's Widget</b>
      </div>

      {/* Progress Bar */}
      <div className="flex-col items-center mb-6 pb-2 hidden">
        <div className="flex items-center space-x-2">
          {[
            { step: 1, label: "Company Info" },
            { step: 2, label: "Financial Info" },
            { step: 3, label: "Health Rating" },
            { step: 4, label: "Funding Options" },
          ]
            .map(({ step, label }) => (
              <div key={step} className="flex items-center">
                <div
                  className={`w-3 h-3 rounded-full transition-all duration-300 relative ${step <= (currentStep + 1) ? "bg-green-500" : "bg-gray-200"
                    }`}
                >
                  <div className="absolute -translate-x-1/2 left-1/2 pt-3 whitespace-nowrap">
                    <span
                      className={`text-sm transition-all duration-300 ${step <= (currentStep + 1)
                        ? "text-green-500 font-medium"
                        : "text-gray-400"
                        }`}
                    >
                      {label}
                    </span>
                  </div>
                </div>
                {step < 4 && (
                  <div
                    className={`w-[120px] h-0.5 mx-2 transition-all duration-300 ${step <= currentStep ? "bg-green-500" : "bg-gray-200"
                      }`}
                  />
                )}
              </div>
            ))}
        </div>
      </div>

      <div className="container relative overflow-x-hidden pb-2">
        <div className="text-center text-white text-xl font-bold bg-primary mb-2 p-2 relative">
          Complete your company metrics and press calculate to view fundability and funding options
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="h-[30px] absolute shadow-sm left-[20px] top-2">
                <CircleHelp width={16} height={16} className="mr-2" />How
                To Use
              </Button>
            </PopoverTrigger>
            <PopoverContent className="md:min-w-[400px]">
              <p className="">
                <ol className="list-decimal list-inside">
                  <li>Use the sliders or enter numbers for each field.</li>
                  <li>
                    Press Calculate to receive your options.
                  </li>
                  <li>
                    You can select different loan types in the loan wheel to
                    view all borrowing options.
                  </li>
                  <li>
                    Once satisfied with the indicative loan terms, press Apply
                    Now to proceed.
                  </li>
                  <li>
                    You will be prompted to create an account and complete an
                    application.
                  </li>
                </ol>
              </p>

              <p className="text-sm">
                <br />{" "}
                Your data will not be released outside of CXM unless you
                permit the application to reach matched lenders, after your
                application has been completed and reviewed.<br />
                <br />{" "}
                You can also reach out to our Loan Specialists to assist you
                with your application at 1-833-296-5363 or{" "}
                <a href="mailto:info@connexmarkets.com">
                  info@connexmarkets.com
                </a>
              </p>
            </PopoverContent>
          </Popover>
        </div>
        <div
          className="flex transition-transform duration-500 ease-in-out"
          style={{
            width: "200%",
          }}
        >
          {(isMobile ? [currentStep] : [1, 2]).map((step) => (
            <div
              key={step}
              className={isMobile ? ("w-1/2 px-3") : ("w-1/4 px-3")}
            >
              {renderStepContent(step as OnboardStep)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default OnboardWidget;
